import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, writeBatch, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface Notification {
  id: string;
  userId: string;
  type: 'like' | 'comment' | 'follow' | 'aging_complete' | 'reminder';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  data?: {
    fromUserId?: string;
    fromUserName?: string;
    fromUserPhoto?: string;
    logId?: string;
    cigarBrand?: string;
    cigarName?: string;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  sendNotification: (userId: string, notification: Omit<Notification, 'id' | 'userId' | 'read' | 'createdAt'>) => Promise<void>;
  requestPermission: () => Promise<boolean>;
  permissionGranted: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
  userId?: string;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children, userId }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const lastNotifiedUnreadIdRef = useRef<string | null>(null);

  // Check permission status
  const checkPermission = useCallback(() => {
    if ('Notification' in window) {
      setPermissionGranted(Notification.permission === 'granted');
    }
  }, []);

  // Initial check and focus listener
  useEffect(() => {
    checkPermission();
    window.addEventListener('focus', checkPermission);
    return () => window.removeEventListener('focus', checkPermission);
  }, [checkPermission]);

  // Subscribe to user's notifications
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs: Notification[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        notifs.push({
          id: doc.id,
          userId: data.userId,
          type: data.type,
          title: data.title,
          message: data.message,
          read: data.read,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          data: data.data,
        });
      });
      setNotifications(notifs);
      setLoading(false);

      // Show browser notification only once per latest unread item to avoid spam.
      const latestUnread = notifs.find(n => !n.read);
      if (!latestUnread) {
        lastNotifiedUnreadIdRef.current = null;
        return;
      }
      if (permissionGranted && document.hidden && lastNotifiedUnreadIdRef.current !== latestUnread.id) {
        try {
          new window.Notification(latestUnread.title, {
            body: latestUnread.message,
            icon: '/logo.jpg',
          });
          lastNotifiedUnreadIdRef.current = latestUnread.id;
        } catch (error) {
          console.error('Failed to display browser notification:', error);
        }
      }
    });

    return () => unsubscribe();
  }, [userId, permissionGranted]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = useCallback(async (notificationId: string) => {
    const notifRef = doc(db, 'notifications', notificationId);
    await updateDoc(notifRef, { read: true });
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    const batch = writeBatch(db);
    notifications
      .filter(n => !n.read)
      .forEach(n => {
        const ref = doc(db, 'notifications', n.id);
        batch.update(ref, { read: true });
      });

    await batch.commit();
  }, [userId, notifications]);

  const sendNotification = useCallback(async (
    targetUserId: string,
    notification: Omit<Notification, 'id' | 'userId' | 'read' | 'createdAt'>
  ) => {
    await addDoc(collection(db, 'notifications'), {
      ...notification,
      userId: targetUserId,
      read: false,
      createdAt: Timestamp.now(),
    });
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      return false;
    }

    const permission = await Notification.requestPermission();
    const granted = permission === 'granted';
    setPermissionGranted(granted);
    return granted;
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        sendNotification,
        requestPermission,
        permissionGranted,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
